import type { AgentObject } from "./api";

// Sui data-shape + explorer helpers. Pure, unit-tested in sui.test.ts.

export function isSuiAgent(a: Pick<AgentObject, "chain">): boolean {
  return (a.chain ?? "solana") === "sui";
}

/** Human-readable action names — used in audit tables, capability chips, and the
 * policy preview so the operator never sees raw `deepbook_swap` ids. */
const ACTION_LABEL: Record<string, string> = {
  deepbook_swap: "Swap",
  deepbook_limit_order: "Limit order",
  deepbook_cancel: "Cancel order",
  transfer: "Transfer",
  revoke: "Revoke",
  pause: "Pause",
  unpause: "Resume",
  // event_type fallbacks (from the audit poller)
  action_allowed: "Action",
  action_denied: "Denied action",
  agent_revoked: "Revoke",
  policy_updated: "Policy update",
  transfer_allowed: "Withdrawal",
  recover: "Revoke + withdraw",
  funds_recovered: "Revoke + withdraw",
};
export function actionLabel(type: string | null | undefined): string {
  if (!type) return "—";
  return ACTION_LABEL[type] ?? type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const SUISCAN = "https://suiscan.xyz/testnet";
export const suiExplorer = {
  object: (id: string) => `${SUISCAN}/object/${id}`,
  tx: (digest: string) => `${SUISCAN}/tx/${digest}`,
  address: (addr: string) => `${SUISCAN}/account/${addr}`,
};

/**
 * Chain-first fleet merge: the backend list is a fast index, but a freshly
 * minted agent whose register call didn't land still exists on-chain and in the
 * device's localStorage refs. Surface those too (deduped by vault) so an agent
 * is never invisible just because the backend missed it. State is still read
 * live from chain on the detail page.
 */
type StoredRef = { agentId: string; agentAddress?: string; name?: string; refs?: { vault?: string; policy?: string; cap?: string }; policy?: Record<string, unknown> };
export function mergeStoredAgents(backend: AgentObject[], stored: StoredRef[]): AgentObject[] {
  const known = new Set(backend.map((a) => a.sui_vault_id).filter(Boolean));
  const seen = new Set<string>();
  const extra: AgentObject[] = [];
  for (const s of stored) {
    const vault = s.refs?.vault;
    if (!vault || known.has(vault) || seen.has(vault)) continue;
    seen.add(vault);
    extra.push({
      id: s.agentId,
      name: s.name ?? s.agentId,
      chain: "sui",
      status: "active",
      framework: "custom",
      sui_vault_id: vault,
      sui_policy_id: s.refs?.policy,
      sui_agent_cap_id: s.refs?.cap,
      agent_address: s.agentAddress,
      policy: s.policy,
    } as unknown as AgentObject);
  }
  return [...backend, ...extra];
}

/** Label an audit row. The provision tx emits policy_updated with no edit
 * descriptor (that's creation); a dashboard edit carries action_descriptor.type. */
export function auditActionLabel(e: { event_type?: string; action_descriptor?: { type?: string } | null }): string {
  if (e.event_type === "policy_updated") return e.action_descriptor?.type ? "Policy update" : "Policy created";
  return actionLabel(e.action_descriptor?.type ?? e.event_type);
}

/** Collapse identical audit rows (e.g. the provision tx emits two PolicyUpdated
 * events) — dedup by tx + event_type + descriptor; denials (null tx) keep their id. */
export function dedupAudit<T extends { id?: string; tx_signature?: string | null; event_type?: string; action_descriptor?: unknown }>(events: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const e of events) {
    const key = `${e.tx_signature ?? e.id}|${e.event_type}|${JSON.stringify(e.action_descriptor ?? {})}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(e);
  }
  return out;
}

export function suiAgentIds(a: AgentObject): {
  vault: string | null;
  policy: string | null;
  cap: string | null;
} {
  return {
    vault: a.sui_vault_id ?? null,
    policy: a.sui_policy_id ?? null,
    cap: a.sui_agent_cap_id ?? null,
  };
}
